import { useState, useEffect } from "react";
import {
  AlertTitle,
  Card,
  CardHeader,
  CardContent,
  Stack,
  TextField,
  Button,
  InputLabel,
  Typography,
  Divider,
  IconButton,
  Popover,
  Box,
  Alert,
  Grid,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stepper,
  Step,
  StepLabel,
  MobileStepper,
} from "@mui/material";
import InvoiceDateRangePicker from "components/Time/InvoiceDateRangePicker";
import { CalendarMonth, ScheduleSend, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { fetchSupabaseClients } from "store/ClientSlice";
import { createSupabaseInvoice } from "store/InvoiceSlice";
import { triggerSystemJob } from "store/SystemJobsSlice";
import { selectPreferences } from "store/PreferenceSlice";
import Loading from "components/Loading";
import ClientPickerAutocomplete from "components/clients/ClientPickerAutocomplete";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import moment from "moment";
import InfoIcon from "@mui/icons-material/Info";

function InvoiceAddForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId } = useSupabaseAuth();
  const theme = useTheme();

  const clients = useSelector((state) => state.clientReducer.clients || []);
  const clientsLoading = useSelector((state) => state.clientReducer.loading);
  const invoices = useSelector((state) => state.invoiceReducer.invoices);
  const loading = useSelector((state) => state.invoiceReducer.loading);
  const preferences = useSelector(selectPreferences);

  // Add new state for display dates
  const [displayDates, setDisplayDates] = useState({
    from_date: "",
    to_date: "",
  });

  // Add state for date picker view toggle
  const [datePickerView, setDatePickerView] = useState("inputs"); // "calendar" or "inputs"

  const [invoice, setInvoice] = useState({
    from_date: "",
    to_date: "",
    client_id: "",
    sent: "no",
    paid: "no",
    notes: "",
    name: "",
    invoice_num: "",
    status: "open",
    invoice_emails: [],
    invoice_cycle: "",
    invoice_processing: "manual",
    type: "", // Will be set after client selection
    budget: "", // Budget amount for revenue
  });

  // Multiple popover states
  const [popoverStates, setPopoverStates] = useState({
    reference: null,
    invoiceNumber: null,
    notes: null,
    // Add more as needed
  });

  // Track if user has clicked continue after entering amount
  const [hasContinuedFromAmount, setHasContinuedFromAmount] = useState(false);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});

  // Help carousel step (only relevant when invoice.type === "hourly")
  const [activeHelpStep, setActiveHelpStep] = useState(0);

  // Utility function to generate name based on type and client
  const generateRevenueName = (clientName, engagementType) => {
    if (!clientName) return "";
    const prefix = engagementType === "project_price"
      ? "Project for"
      : (engagementType === "retainer_monthly" || engagementType === "retainer_rolling")
        ? "Retainer for"
        : "Invoice for";
    return `${prefix} ${clientName}`;
  };

  // Utility functions for date formatting
  const formatDateForDisplay = (date) => {
    if (!date) return "";
    // Handle YYYY-MM-DD strings directly, or parse as Date if needed
    // moment can parse both formats without timezone issues
    return moment(date).format("YYYY-MM-DD");
  };

  const formatDateForStorage = (date, isEndDate = false) => {
    if (!date) return "";
    // Format as YYYY-MM-DD (ISO date format) without timezone or time component
    return moment(date).format("YYYY-MM-DD");
  };

  const helpContent = [
    {
      title: "Manual vs Auto-Invoicing",
      content:
        "Invoices can be created manually using this workflow or they can be created automatically when you enter a calendar event.",
    },
    {
      title: "Default Settings",
      content:
        "If you set your default settings, new clients will be set up with the invoicing cadence you want for most clients. You can change this on a per-client basis.",
    },
    {
      title: "Automatic Invoice Creation",
      content:
        "When a client is set up for invoicing, the system will create an invoice using the preferred calendar when you enter a calendar event.",
    },
    {
      title: "Event-Driven Invoicing",
      content:
        "Invoicing is event-driven. If you do not have activity for a time, then the system does nothing. But when you start work, the system will use the client's preferences and automatically set up an invoice.",
    },
    {
      title: "Invoice Cadences",
      content:
        "The system handles three invoice processes: Weekly, BiWeekly, or Monthly. Invoices start on Monday and end on Sunday, and align with the start of the month.",
    },
  ];

  // Handle date changes from CalendarDateRangePicker
  const handleCalendarDateChange = (values) => {
    console.log("Calendar date change:", values);
    const [fromDate, toDate] = values;

    // Update the display dates for the input fields
    setDisplayDates({
      from_date: formatDateForDisplay(fromDate),
      to_date: formatDateForDisplay(toDate),
    });

    // Update the invoice state with the formatted dates for storage
    setInvoice((prev) => ({
      ...prev,
      from_date: formatDateForStorage(fromDate),
      to_date: formatDateForStorage(toDate, true),
    }));
  };

  // Handle individual text field date changes
  const handleDateFieldChange = (event) => {
    const { name, value } = event.target;
    console.log("text field change", { name, value });

    // Update display dates
    setDisplayDates((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Pass the YYYY-MM-DD string directly to formatDateForStorage
    // Don't use new Date() as it interprets as UTC and causes timezone shifts
    // formatDateForStorage can handle YYYY-MM-DD strings directly
    const dateValue = value || null;

    // Update the invoice state
    setInvoice((prev) => ({
      ...prev,
      [name]: formatDateForStorage(dateValue, name === "to_date"),
    }));
  };

  // Handle date picker view toggle
  const handleDatePickerViewChange = (event, newView) => {
    if (newView !== null) {
      setDatePickerView(newView);
    }
  };

  // Handle Today button click for input fields
  const handleTodayClick = () => {
    const today = new Date();
    const todayFormatted = formatDateForDisplay(today);

    setDisplayDates((prev) => ({
      ...prev,
      from_date: todayFormatted,
    }));

    setInvoice((prev) => ({
      ...prev,
      from_date: formatDateForStorage(today, false),
    }));
  };

  // Handle Clear button click for input fields
  const handleClearClick = () => {
    setDisplayDates({
      from_date: "",
      to_date: "",
    });

    setInvoice((prev) => ({
      ...prev,
      from_date: "",
      to_date: "",
    }));
  };

  // Keep the regular handleChange for non-date fields
  const handleChange = (event) => {
    const { name, value } = event.target;
    // Only handle non-date fields
    if (name !== "from_date" && name !== "to_date") {
      setInvoice((prev) => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    if ((!clients || clients.length === 0) && userId) {
      dispatch(fetchSupabaseClients({ userId }));
    }
  }, [dispatch, clients, userId]);

  // Check for client_id in URL search params and pre-select the client
  useEffect(() => {
    const clientIdFromUrl = searchParams.get("client_id");
    if (clientIdFromUrl && clients && clients.length > 0) {
      const selectedClient = clients.find((client) => client.id === clientIdFromUrl);
      if (selectedClient) {
        setInvoice((prev) => {
          // Only update if client_id hasn't been set yet or if it's different
          if (!prev.client_id || prev.client_id !== clientIdFromUrl) {
            return {
              ...prev,
              client_id: clientIdFromUrl,
              name: generateRevenueName(selectedClient?.name || "", prev.type || ""),
              rate: selectedClient?.rate || "",
              invoice_emails: selectedClient?.invoice_emails || [],
              invoice_cycle: selectedClient?.invoice_cycle || "none",
              invoice_processing: "manual",
            };
          }
          return prev;
        });
      }
    }
  }, [searchParams, clients]);


  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    console.log("Form submission - invoice data:", invoice);

    // Clear previous validation errors
    setValidationErrors({});

    // Validate required fields
    const errors = {};

    if (!invoice.client_id) {
      errors.client_id = "Client is required";
    }
    if (!invoice.from_date) {
      errors.from_date = "From date is required";
    }
    if (!invoice.to_date) {
      errors.to_date = "To date is required";
    }
    // Validate invoice_emails when auto processing is selected
    if (invoice.invoice_processing === "invoice" && (!invoice.invoice_emails || invoice.invoice_emails.length === 0)) {
      errors.invoice_emails = "At least one invoice email is required when automatic processing is selected";
    }
    // Validate budget field based on invoice type
    if ((invoice.type === "project_price" || invoice.type === "retainer_monthly") && !invoice.budget) {
      errors.budget = "Budget amount is required";
    }

    // If there are validation errors, set them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    dispatch(createSupabaseInvoice({ userId, ...invoice }))
      .then(async (result) => {
        console.log("Invoice created successfully:", result);

        // Trigger INVOICE_CALCULATE_STATS event if invoice was created successfully
        if (result.type === 'data/createSupabaseInvoice/fulfilled' && result.payload?.id) {
          const invoiceId = result.payload.id;
          try {
            await dispatch(triggerSystemJob({
              eventName: 'INVOICE_CALCULATE_STATS',
              data: {
                invoice_id: invoiceId,
                uid: userId,
              },
            })).unwrap();
          } catch (triggerError) {
            console.error("Error triggering INVOICE_CALCULATE_STATS:", triggerError);
            // Don't block navigation if event trigger fails
          }

          // Check if user is in onboarding mode
          const setupStatus = preferences?.setupStatus;
          const isInOnboarding = setupStatus !== "skipped" && setupStatus !== "complete";

          if (isInOnboarding) {
            // User is in onboarding, redirect back to onboarding
            navigate("/onboarding");
          } else {
            // Normal flow: Navigate to the invoice detail page
            navigate(`/revenue/${invoiceId}`);
          }
        } else {
          // Fallback navigation if invoice creation didn't return an ID
          const setupStatus = preferences?.setupStatus;
          const isInOnboarding = setupStatus !== "skipped" && setupStatus !== "complete";

          if (isInOnboarding) {
            navigate("/onboarding");
          } else if (invoices.length === 0) {
            navigate(`/dashboard`);
          } else {
            navigate(`/revenue`);
          }
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
      });
  };

  // Generic popover handlers for multiple popovers
  const handlePopoverClick = (event, popoverKey) => {
    setPopoverStates((prev) => ({
      ...prev,
      [popoverKey]: event.currentTarget,
    }));
  };

  const handlePopoverClose = (popoverKey) => {
    setPopoverStates((prev) => ({
      ...prev,
      [popoverKey]: null,
    }));
  };

  const applyClientSelection = (clientId) => {
    const selectedClient = clients.find((client) => client.id === clientId);
    setInvoice((prev) => ({
      ...prev,
      client_id: clientId,
      name: generateRevenueName(selectedClient?.name || "", prev.type || ""),
      rate: selectedClient?.rate || "",
      invoice_emails: selectedClient?.invoice_emails || [],
      invoice_cycle: selectedClient?.invoice_cycle || "biweekly",
      invoice_processing: "manual",
      type: "",
      budget: "",
    }));
    setHasContinuedFromAmount(false);
  };

  const handleInvoiceTypeChange = (event) => {
    const { value } = event.target;
    setInvoice((prev) => {
      // Find client name to update the name field
      const selectedClient = clients.find((client) => client.id === prev.client_id);
      return {
        ...prev,
        type: value,
        // Update name based on new type
        name: selectedClient ? generateRevenueName(selectedClient.name || "", value) : prev.name,
        // Clear budget when changing type (except if switching to a type that also needs budget)
        budget: (value === "project_price" || value === "retainer_monthly") ? prev.budget : "",
      };
    });
    // Reset continue state when changing type
    setHasContinuedFromAmount(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "300px",
          maxWidth: "600px",
          margin: "auto",
        }}
      >
        <Loading text="Saving..." />
      </Box>
    );
  }

  return (
    <Box maxWidth="md" sx={{ py: 2, mx: "auto" }}>
      {!invoice.client_id ? (
        <Grid container justifyContent="center">
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 10,
              lg: 8,
            }}
          >
            <Card>
              <CardHeader title="Create New Revenue Item" />
              <CardContent>
                <Stack spacing={1}>
                  <ClientPickerAutocomplete
                    id="engagement-add-client"
                    clients={clients}
                    value={invoice.client_id || ""}
                    onValueChange={applyClientSelection}
                    sentinelValue=""
                    sentinelLabel="Select a client…"
                    loading={clientsLoading}
                    label="Select client"
                    placeholder="Search or select a client…"
                    size="medium"
                  />
                  <Typography variant="caption" color="textSecondary">
                    The first step is to select the client for this engagement.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : !invoice.type ||
        ((invoice.type === "project_price" || invoice.type === "retainer_monthly") && (!invoice.budget || !hasContinuedFromAmount)) ||
        ((invoice.type === "hourly" || invoice.type === "retainer_rolling") && !hasContinuedFromAmount) ? (
        <Grid container justifyContent="center">
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 10,
              lg: 8,
            }}
          >
            <Card>
              <CardHeader title="Create New Revenue Item" />
              <CardContent>
                <Stack spacing={3}>
                  {/* Show Selected Client */}
                  <Box>
                    <InputLabel>Selected Client</InputLabel>
                    <TextField
                      value={
                        clients.find((c) => c.id === invoice.client_id)?.name || ""
                      }
                      fullWidth
                      disabled
                      sx={{ mt: 0 }}
                      InputProps={{
                        endAdornment: (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                              setInvoice((prev) => ({
                                ...prev,
                                client_id: "",
                                type: "",
                              }));
                            }}
                          >
                            Change
                          </Button>
                        ),
                      }}
                    />
                  </Box>

                  <Divider />

                  {/* Invoice Type Selection as Panels */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Select Engagement Type
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      How do you want to track work and value for this engagement?
                    </Typography>
                    <RadioGroup
                      name="type"
                      value={invoice.type || ""}
                      onChange={handleInvoiceTypeChange}
                      sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}
                    >
                      <Box
                        sx={{
                          flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' },
                          border: 2,
                          borderColor: invoice.type === "hourly" ? "primary.main" : "divider",
                          borderRadius: 2,
                          p: 2,
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => handleInvoiceTypeChange({ target: { name: "type", value: "hourly" } })}
                      >
                        <FormControlLabel
                          value="hourly"
                          control={<Radio />}
                          label={
                            <Typography variant="h6">
                              Hourly
                            </Typography>
                          }
                          sx={{ m: 0, mb: 1, width: "100%" }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                          Bill based on hours worked. Track time in calendar events and invoice for actual hours.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' },
                          border: 2,
                          borderColor: invoice.type === "project_price" ? "primary.main" : "divider",
                          borderRadius: 2,
                          p: 2,
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => handleInvoiceTypeChange({ target: { name: "type", value: "project_price" } })}
                      >
                        <FormControlLabel
                          value="project_price"
                          control={<Radio />}
                          label={
                            <Typography variant="h6">
                              Project Price
                            </Typography>
                          }
                          sx={{ m: 0, mb: 1, width: "100%" }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                          Fixed price for a project or deliverable. Set a total amount regardless of hours worked.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' },
                          border: 2,
                          borderColor: invoice.type === "retainer_monthly" ? "primary.main" : "divider",
                          borderRadius: 2,
                          p: 2,
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => handleInvoiceTypeChange({ target: { name: "type", value: "retainer_monthly" } })}
                      >
                        <FormControlLabel
                          value="retainer_monthly"
                          control={<Radio />}
                          label={
                            <Typography variant="h6">
                              Monthly Retainer
                            </Typography>
                          }
                          sx={{ m: 0, mb: 1, width: "100%" }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                          Monthly retainer agreement. Fixed amount billed each month on a recurring schedule.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' },
                          border: 2,
                          borderColor: invoice.type === "retainer_rolling" ? "primary.main" : "divider",
                          borderRadius: 2,
                          p: 2,
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => handleInvoiceTypeChange({ target: { name: "type", value: "retainer_rolling" } })}
                      >
                        <FormControlLabel
                          value="retainer_rolling"
                          control={<Radio />}
                          label={
                            <Typography variant="h6">
                              Rolling Retainer
                            </Typography>
                          }
                          sx={{ m: 0, mb: 1, width: "100%" }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                          Rolling retainer agreement. Fixed amount billed based on a rolling time period (e.g., every 30 days).
                        </Typography>
                      </Box>
                    </RadioGroup>
                  </Box>

                  {/* Budget Input Field */}
                  {(invoice.type === "project_price" || invoice.type === "retainer_monthly") && (
                    <Box>
                      <InputLabel>Budget *</InputLabel>
                      <TextField
                        type="number"
                        name="budget"
                        value={invoice.budget || ""}
                        onChange={handleChange}
                        fullWidth
                        required
                        placeholder="0.00"
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                        }}
                        helperText={
                          invoice.type === "project_price"
                            ? "Enter the total fixed price for this project"
                            : "Enter the monthly retainer amount"
                        }
                      />
                    </Box>
                  )}

                  {/* Continue Button - show when type is selected and amount is entered if required */}
                  {invoice.type && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        disabled={
                          ((invoice.type === "project_price" || invoice.type === "retainer_monthly") && !invoice.budget)
                        }
                        onClick={() => {
                          setHasContinuedFromAmount(true);
                        }}
                      >
                        Continue to Engagement Details
                      </Button>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardHeader
            title={
              invoice.type === "project_price"
                ? "Create New Project"
                : (invoice.type === "retainer_monthly" || invoice.type === "retainer_rolling")
                  ? "Create New Retainer"
                  : "Create New Invoice"
            }
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Engagement Type:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {invoice.type === "hourly" ? "Hourly" :
                    invoice.type === "project_price" ? "Project Price" :
                      invoice.type === "retainer_monthly" ? "Monthly Retainer" :
                        invoice.type === "retainer_rolling" ? "Rolling Retainer" :
                          invoice.type}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setInvoice((prev) => ({
                      ...prev,
                      type: "",
                    }));
                    setHasContinuedFromAmount(false);
                  }}
                  sx={{ ml: 1 }}
                >
                  Change
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack direction="column" spacing={3}>
                {/* Validation Errors Alert */}
                {Object.keys(validationErrors).length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2" component="div">
                      Please fix the following errors:
                      <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                        {Object.values(validationErrors).map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </Typography>
                  </Alert>
                )}
                {/* Client */}
                <Box>
                  <ClientPickerAutocomplete
                    id="engagement-form-client"
                    clients={clients}
                    value={invoice.client_id || ""}
                    onValueChange={applyClientSelection}
                    sentinelValue=""
                    sentinelLabel="Select…"
                    loading={clientsLoading}
                    label={
                      invoice.type === "project_price"
                        ? "Client for this project"
                        : invoice.type === "retainer_monthly" || invoice.type === "retainer_rolling"
                          ? "Client for this retainer"
                          : "Client for this invoice"
                    }
                    placeholder="Search or select a client…"
                    size="medium"
                  />
                </Box>


                {/* Reference and Invoice Number on same line */}
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: '0 0 70%', width: { xs: '100%', sm: '70%' } }}>
                    <InputLabel>Reference Name</InputLabel>
                    <TextField
                      sx={{ mt: 0 }}
                      name="name"
                      value={invoice.name}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            aria-describedby="reference-popover"
                            onClick={(event) =>
                              handlePopoverClick(event, "reference")
                            }
                            size="small"
                            style={{ marginLeft: "4px" }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                      fullWidth
                      required
                      placeholder="Reference Name"
                    />

                    <Popover
                      id="reference-popover"
                      open={Boolean(popoverStates.reference)}
                      anchorEl={popoverStates.reference}
                      onClose={() => handlePopoverClose("reference")}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      slotProps={{

                        paper: {
                          sx: {
                            maxWidth: '400px', // Set your desired max width here
                            width: 'fit-content', // Optional: size to content while respecting max-width
                          },
                        },
                      }}
                    >
                      <Typography sx={{ p: 2 }}>
                        Enter a human readable name that will appear on reports
                        etc.
                      </Typography>
                    </Popover>
                  </Box>
                  <Box sx={{ flex: '0 0 30%', width: { xs: '100%', sm: '30%' } }}>
                    <InputLabel>
                      {invoice.type === "project_price"
                        ? "Project Number"
                        : (invoice.type === "retainer_monthly" || invoice.type === "retainer_rolling")
                          ? "Retainer Number"
                          : "Invoice Number"}
                    </InputLabel>
                    <TextField
                      sx={{ mt: 0 }}
                      type="number"
                      name="invoice_num"
                      value={invoice.invoice_num || ""}
                      onChange={handleChange}
                      placeholder="Leave empty to auto-generate"
                      fullWidth
                      helperText={
                        invoice.type === "project_price"
                          ? "Leave empty to automatically generate the next project number"
                          : (invoice.type === "retainer_monthly" || invoice.type === "retainer_rolling")
                            ? "Leave empty to automatically generate the next retainer number"
                            : "Leave empty to automatically generate the next invoice number"
                      }
                      inputProps={{
                        min: 1,
                        step: 1
                      }}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            aria-describedby="invoice-number-popover"
                            onClick={(event) =>
                              handlePopoverClick(event, "invoiceNumber")
                            }
                            size="small"
                            style={{ marginLeft: "4px" }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />

                    <Popover
                      id="invoice-number-popover"
                      open={Boolean(popoverStates.invoiceNumber)}
                      anchorEl={popoverStates.invoiceNumber}
                      onClose={() => handlePopoverClose("invoiceNumber")}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            maxWidth: '400px', // Set your desired max width here
                            width: 'fit-content', // Optional: size to content while respecting max-width
                          },
                        },
                      }}
                    >
                      <Typography sx={{ p: 2 }}>
                        {invoice.type === "project_price"
                          ? "Enter a custom project number, or leave empty to automatically generate the next sequential number (1, 2, 3...). Project numbers must be unique per account."
                          : (invoice.type === "retainer_monthly" || invoice.type === "retainer_rolling")
                            ? "Enter a custom retainer number, or leave empty to automatically generate the next sequential number (1, 2, 3...). Retainer numbers must be unique per account."
                            : "Enter a custom invoice number, or leave empty to automatically generate the next sequential number (1, 2, 3...). Invoice numbers must be unique per account."}
                      </Typography>
                    </Popover>
                  </Box>
                </Box>

                <Divider />

                {/* Date Options */}
                <Box>
                  <InvoiceDateRangePicker
                    invoice={invoice}
                    displayDates={displayDates}
                    datePickerView={datePickerView}
                    onDatePickerViewChange={handleDatePickerViewChange}
                    onCalendarDateChange={handleCalendarDateChange}
                    onDateFieldChange={handleDateFieldChange}
                    onTodayClick={handleTodayClick}
                    onClearClick={handleClearClick}
                  />
                </Box>
              </Stack>

              {invoice.from_date && invoice.to_date && (
                <Box sx={{ mt: 3 }}>
                  <Alert
                    variant="outlined"
                    icon={<CalendarMonth />}
                    sx={{ mb: 2 }}
                  >
                    This invoice spans{" "}
                    <b>
                      {moment(invoice.to_date).diff(
                        moment(invoice.from_date),
                        "days"
                      )}{" "}
                      days{" "}
                    </b>
                    from {moment(invoice.from_date).format("dddd MM/DD/YY")}
                    &nbsp; to &nbsp;
                    {moment(invoice.to_date).format("dddd MM/DD/YY")}.
                  </Alert>
                  <Alert variant="outlined" icon={<ScheduleSend />}>
                    <i>
                      Expect this engagement to close
                      {moment(invoice.to_date).diff(moment(), "days") > 0 &&
                        " in"}{" "}
                      <b>
                        {Math.abs(
                          moment(invoice.to_date).diff(moment(), "days")
                        )}
                        {Math.abs(
                          moment(invoice.to_date).diff(moment(), "days")
                        ) > 1
                          ? " days"
                          : " day"}
                        {moment(invoice.to_date).diff(moment(), "days") < 0 &&
                          " ago"}
                        .
                      </b>
                    </i>
                  </Alert>
                </Box>
              )}

              <Divider sx={{ mt: 3 }} />

              {/* Notes */}
              <Box sx={{ mt: 3 }}>
                <InputLabel>Internal Notes</InputLabel>
                <TextField
                  value={invoice.notes || ""}
                  name="notes"
                  onChange={handleChange}
                  fullWidth
                  placeholder="Add any additional notes."
                  multiline
                  rows={3}
                />

              </Box>


              <Divider />

              <Typography variant="h4" sx={{ mt: 2, mb: 2 }} gutterBottom>
                How will this engagement be processed?
              </Typography>

              {/* Show tips only when Hourly is selected */}
              {invoice.type === "hourly" && (
                <Box sx={{ mt: 2 }}>

                  {/* <Stepper
                    activeStep={activeHelpStep}
                    sx={{ pt: 0, pb: 2 }}
                    alternativeLabel
                  >
                    {helpContent.map((item) => (
                      <Step key={item.title}>
                        <StepLabel>{item.title}</StepLabel>
                      </Step>
                    ))}
                  </Stepper> */}
                  <Card variant="outlined" sx={{ p: 0 }}>
                    <CardHeader
                      title={
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          {helpContent[activeHelpStep].title}
                        </Typography>
                      }
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <Typography variant="body2" component="div">
                        {helpContent[activeHelpStep].content}
                      </Typography>
                    </CardContent>
                  </Card>
                  <MobileStepper
                    variant="dots"
                    steps={helpContent.length}
                    position="static"
                    activeStep={activeHelpStep}
                    sx={{ mt: 1, mb: 0, px: 0, bgcolor: "transparent" }}
                    nextButton={
                      <Button
                        size="small"
                        onClick={() => setActiveHelpStep((s) => Math.min(s + 1, helpContent.length - 1))}
                        disabled={activeHelpStep === helpContent.length - 1}
                      >
                        Next
                        <KeyboardArrowRight />
                      </Button>
                    }
                    backButton={
                      <Button
                        size="small"
                        onClick={() => setActiveHelpStep((s) => Math.max(s - 1, 0))}
                        disabled={activeHelpStep === 0}
                      >
                        <KeyboardArrowLeft />
                        Back
                      </Button>
                    }
                  />

                </Box>
              )}


              {/* Invoice Processing Options */}
              <Box sx={{ mt: 2, mb: 2 }}>
                <RadioGroup
                  name="invoice_processing"
                  value={invoice.invoice_processing || "manual"}
                  onChange={handleChange}
                  sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      border: 2,
                      borderColor: invoice.invoice_processing === "manual" ? "primary.main" : "divider",
                      borderRadius: 2,
                      p: 2,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => handleChange({ target: { name: "invoice_processing", value: "manual" } })}
                  >
                    <FormControlLabel
                      value="manual"
                      control={<Radio />}
                      label={
                        <Typography variant="h6">
                          Manual Processing? (Default)
                        </Typography>
                      }
                      sx={{ m: 0, mb: 1, width: "100%" }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                      This invoice will be held for manual review and closing. The online will
                      close but nothing will be sent to the client. You will need to manually send
                      the invoice to the client.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      border: 2,
                      borderColor: invoice.invoice_processing === "invoice" ? "primary.main" : "divider",
                      borderRadius: 2,
                      p: 2,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => handleChange({ target: { name: "invoice_processing", value: "invoice" } })}
                  >
                    <FormControlLabel
                      value="invoice"
                      control={<Radio />}
                      label={
                        <Typography variant="h6">
                          Automatically Process?
                        </Typography>
                      }
                      sx={{ m: 0, mb: 1, width: "100%" }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 4.5 }}>
                      This invoice will be automatically processed and closed. This will
                      send the invoice to the client via email once the invoice is created
                      on the day after the invoice cycle ends.
                    </Typography>
                  </Box>
                </RadioGroup>
              </Box>

              {/* Show the invoice emails textarea only when auto processing is selected */}
              {invoice.invoice_processing === "invoice" && (
                <Box sx={{ mt: 2 }}>
                  <InputLabel>Invoice Email Recipients *</InputLabel>
                  <TextField
                    name="invoice_emails"
                    value={Array.isArray(invoice.invoice_emails)
                      ? invoice.invoice_emails.join('\n')
                      : (invoice.invoice_emails || '')}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Convert textarea value (newline-separated) to array, filter out empty strings
                      const emailArray = value
                        .split('\n')
                        .map(email => email.trim())
                        .filter(email => email.length > 0);
                      handleChange({ target: { name: "invoice_emails", value: emailArray } });
                      // Clear validation error when user starts typing
                      if (validationErrors.invoice_emails) {
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.invoice_emails;
                          return newErrors;
                        });
                      }
                    }}
                    fullWidth
                    multiline
                    rows={4}
                    required
                    placeholder="Enter email addresses, one per line"
                    helperText={validationErrors.invoice_emails || "Enter the email addresses that will receive this invoice. One email per line."}
                    error={!!validationErrors.invoice_emails || (invoice.invoice_processing === "invoice" && (!invoice.invoice_emails || invoice.invoice_emails.length === 0))}
                  />
                  {invoice.client_id && (() => {
                    const selectedClient = clients.find((client) => client.id === invoice.client_id);
                    const clientEmails = selectedClient?.invoice_emails || [];
                    if (clientEmails.length > 0 && (!invoice.invoice_emails || invoice.invoice_emails.length === 0)) {
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Suggested: {clientEmails.join(', ')}
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                </Box>
              )}

              {/* Submit Button */}
              <Box sx={{ mt: 3 }}>
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  Create Engagement
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default InvoiceAddForm;
